import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { supabase } from "../../supabaseClient";
import VendorCard from "../../components/Vendor-app-card";
import AlphabetScrubberRail from "../../components/AlphabetScrubberRail"; // Decoupled Component
import "./AdminVendors.css";

const normalizeTag = (cat) => {
  if (typeof cat === "string") return cat.replace(/^"|"$/g, "");
  if (cat && typeof cat === "object" && "label" in cat) return cat.label;
  return String(cat ?? "");
};

const TENT_DISPLAY_MAP = {
  "20x20_single": "20×20 Individual",
  "20x20_shared": "20×20 Shared",
  "10x10_single": "10×10 Individual",
  "food_court":   "Food Court",
  "pod_shared":   "17×17 Pod",
  "20x20_open":   "20×20 Open",
  "sponsored":    "Sponsored",
  "biz_boost":    "Biz Boost"
};

const getDenialEmailHtml = (brandName) => `
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
</head>
<body style="margin:0;padding:0;background-color:#f4f5f7;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f5f7;padding:40px 16px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e8eaed;">
        <tr><td style="height:4px;background-color:#6b7280;font-size:0;line-height:0;">&nbsp;</td></tr>
        <tr><td align="center" style="padding:32px 40px 24px;">
          <p style="margin:0;font-size:18px;font-weight:bold;color:#111827;">BizBaz Events</p>
          <p style="margin:4px 0 0;font-size:12px;color:#6b7280;">Christmas Festival 2026</p>
        </td></tr>
        <tr><td style="padding:0 40px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="height:1px;background-color:#e8eaed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr>
        <tr><td style="padding:32px 40px;">
          <p style="margin:0 0 16px;font-size:16px;color:#111827;">Hi <strong>${brandName}</strong>,</p>
          <p style="margin:0 0 16px;font-size:15px;color:#374151;line-height:1.6;">
            Thank you for applying to be a vendor at the <strong style="color:#111827;">Bizarre Bazaar Christmas Festival 2026</strong>. After reviewing all applications, we are unable to offer you a spot on this occasion. 
          </p>
          <p style="margin:0 0 24px;font-size:15px;color:#374151;line-height:1.6;">
           Each edition has a fixed number of spots.
          </p>
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:28px;background-color:#f9fafb;border:1px solid #e8eaed;border-radius:8px;">
            <tr><td style="padding:18px 20px;">
              <p style="margin:0 0 6px;font-size:13px;font-weight:bold;color:#111827;">You're on our radar</p>
              <p style="margin:0;font-size:14px;color:#374151;line-height:1.6;">
                If a spot opens up for the Bizarre Bazaar Christmas Festival, we will reach out to you directly. We will also keep you informed on other participation opportunities in future editions.
              </p>
            </td></tr>
          </table>
          <p style="margin:0 0 4px;font-size:14px;color:#6b7280;">You remain a valued part of the BizBaz vendor community and we appreciate your continued support.</p>
          <p style="margin:0 0 28px;font-size:14px;color:#6b7280;">— The BizBaz Events Team</p>
        </td></tr>
        <tr><td style="padding:0 40px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="height:1px;background-color:#e8eaed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr>
        <tr><td align="center" style="padding:20px 40px 28px;">
          <p style="margin:0;font-size:11px;color:#9ca3af;">
            &copy; 2026 BizBaz Events &nbsp;&middot;&nbsp;
            <a href="https://bizbazevents.com/privacy-policy" style="color:#9ca3af;text-decoration:underline;">Privacy Policy</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>
`;

const AdminVendors = () => {
  const [events, setEvents]               = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [applications, setApplications]   = useState([]);
  const [loading, setLoading]             = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [animating, setAnimating]         = useState(false);

  // Filters
  const [query, setQuery]                 = useState("");
  const [statusFilter, setStatusFilter]   = useState("all");
  const [tentFilter, setTentFilter]       = useState("all");
  const [newOnly, setNewOnly]             = useState(false);
  const [selectedTags, setSelectedTags]   = useState([]);

  // Sorting
  const [sortBy, setSortBy]               = useState("alpha");
  const [showAllTags, setShowAllTags]     = useState(false);
  const [activeLetter, setActiveLetter]   = useState("A");

  const sectionRefs = useRef({});

  useEffect(() => {
    const fetchEvents = async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date")
        .order("start_date", { ascending: false });

      if (error) { console.error(error); setEventsLoading(false); return; }

      setEvents(data || []);
      const global = data?.find(ev => ev.name?.toLowerCase().includes("christmas"));
      setSelectedEvent(global?.id || data?.[0]?.id);
      setEventsLoading(false);
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;

    const fetchApplications = async () => {
      setLoading(true);
      setAnimating(false);

      const { data, error } = await supabase
        .from("vendor_applications")
        .select(`
          id,
          status,
          tent_type,
          description,
          whatsapp_number,
          location,
          created_at,
          is_first_application,
          categories,
          deposit_submitted,
          deposit_confirmed,
          deposit_declared_amount,
          balance_submitted,
          balance_confirmed,
          balance_declared_amount,
          invoice_total,
          custom_base_price,
          deposit_required,
          chosen_spot,
          extras,
          vendor:vendor_id (
            id,
            brand_name,
            contact_person,
            email,
            phone_number,
            website,
            instagram,
            facebook,
            logo_url,
            slug
          )
        `)
        .eq("event_id", selectedEvent)
        .order("created_at", { ascending: true });

      if (error) { console.error(error); setLoading(false); return; }

      setApplications(data || []);
      setLoading(false);
      setTimeout(() => setAnimating(true), 50);
    };

    fetchApplications();
  }, [selectedEvent]);

  const handleStatusChange = async (applicationId, newStatus) => {
    const { error } = await supabase
      .from("vendor_applications")
      .update({ status: newStatus })
      .eq("id", applicationId);

    if (error) { alert("Failed to update status"); return; }

    setApplications((prev) =>
      prev.map((a) => (a.id === applicationId ? { ...a, status: newStatus } : a))
    );

    if (newStatus === "denied") {
      const app       = applications.find(a => a.id === applicationId);
      const email     = app?.vendor?.email;
      const brandName = app?.vendor?.brand_name || "there";

      if (email) {
        fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to:      email,
            subject: "Your BizBaz Christmas  Festival 2026 Application",
            html:    getDenialEmailHtml(brandName),
          }),
        }).catch(err => console.error("Denial email failed:", err));
      }
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    const { data, error } = await supabase
      .from("vendor_applications")
      .delete()
      .eq("id", applicationId)
      .select("id");

    if (error) { alert("Failed to delete application: " + error.message); return; }

    if (!data || data.length === 0) {
      alert("Delete failed — RLS restricted.");
      return;
    }

    setApplications((prev) => prev.filter((a) => a.id !== applicationId));
  };

  const handleExportExcel = () => {
    const eventName = events.find(ev => ev.id === selectedEvent)?.name || "Event";

    const rows = filtered.map((a) => ({
      "Brand Name":        a.vendor?.brand_name        || "",
      "Contact Person":    a.vendor?.contact_person    || "",
      "Email":             a.vendor?.email             || "",
      "Phone":             a.vendor?.phone_number      || "",
      "WhatsApp":          a.whatsapp_number           || "",
      "Instagram":         a.vendor?.instagram         || "",
      "Facebook":          a.vendor?.facebook          || "",
      "Website":           a.vendor?.website           || "",
      "Tent Type":         TENT_DISPLAY_MAP[a.tent_type] || a.tent_type || "",
      "Location":          a.location                  || "",
      "Status":            a.status                    || "pending",
      "New Vendor":        a.is_first_application      ? "Yes" : "No",
      "Deposit Submitted": a.deposit_submitted         ? "Yes" : "No",
      "Deposit Confirmed": a.deposit_confirmed         ? "Yes" : "No",
      "Balance Submitted": a.balance_submitted         ? "Yes" : "No",
      "Balance Confirmed": a.balance_confirmed         ? "Yes" : "No",
      "Invoice Total":     a.invoice_total             ?? "",
      "Applied At":        a.created_at ? new Date(a.created_at).toLocaleDateString() : "",
      "Description":       a.description               || "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String(r[key] || "").length)) + 2,
    }));
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendors");
    XLSX.writeFile(wb, `${eventName.replace(/\s+/g, "_")}_Vendors_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const tentTypeCounts = useMemo(() => {
    const counts = {};
    applications.forEach((a) => {
      if (a.tent_type) counts[a.tent_type] = (counts[a.tent_type] || 0) + 1;
    });
    return counts;
  }, [applications]);

  const { topTags, allTags } = useMemo(() => {
    const counts = {};
    applications.forEach((a) => {
      (a.categories || []).forEach((c) => {
        const norm = normalizeTag(c);
        if (norm) counts[norm] = (counts[norm] || 0) + 1;
      });
    });

    const sortedTags = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
    return {
      topTags: sortedTags.slice(0, 5),
      allTags: sortedTags.sort()
    };
  }, [applications]);

  const filtered = useMemo(() => {
    let result = [...applications];

    if (query) {
      const q = query.toLowerCase();
      result = result.filter((a) =>
        a.vendor?.brand_name?.toLowerCase().includes(q) ||
        a.vendor?.contact_person?.toLowerCase().includes(q) ||
        a.vendor?.email?.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((a) => a.status === statusFilter);
    }

    if (tentFilter !== "all") {
      result = result.filter((a) => a.tent_type === tentFilter);
    }

    if (newOnly) {
      result = result.filter((a) => a.is_first_application);
    }

    if (selectedTags.length > 0) {
      result = result.filter((a) => {
        const appTags = (a.categories || []).map(normalizeTag);
        return selectedTags.some((t) => appTags.includes(t));
      });
    }

    if (sortBy === "alpha") {
      result.sort((a, b) => {
        const nameA = (a.vendor?.brand_name || "").toLowerCase();
        const nameB = (b.vendor?.brand_name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else {
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }

    return result;
  }, [applications, query, statusFilter, tentFilter, newOnly, selectedTags, sortBy]);

  const groupedByLetter = useMemo(() => {
    if (sortBy !== "alpha") return null;

    const groups = {};
    filtered.forEach((app) => {
      const rawName = app.vendor?.brand_name || "";
      const firstChar = rawName[0] ? rawName[0].toUpperCase() : "#";
      const letter = /[A-Z]/.test(firstChar) ? firstChar : "#";

      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(app);
    });

    return groups;
  }, [filtered, sortBy]);

  // Handle direct letter jumps
  const handleSelectLetter = useCallback((letter) => {
    setActiveLetter(letter);
    const el = sectionRefs.current[letter];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const approvedCount  = applications.filter((a) => a.status === "approved").length;
  const pendingCount   = applications.filter((a) => !a.status || a.status === "pending").length;
  const deniedCount    = applications.filter((a) => a.status === "denied").length;
  const newVendorCount = applications.filter((a) => a.is_first_application).length;

  const displayTags = showAllTags ? allTags : topTags;

  return (
    <div className="av-page">
      <div className={`av-container ${animating ? "av-loaded" : ""}`}>

        {/* Header */}
        <div className="av-header">
          <div>
            <h1 className="av-header-title">Vendor Applications</h1>
            <p className="av-header-sub">Review and manage vendor applications per event.</p>
          </div>

          <div className="av-header-controls">
            {!eventsLoading && events.length > 0 && (
              <select
                className="av-event-select"
                value={selectedEvent || ""}
                onChange={(e) => {
                  setQuery("");
                  setStatusFilter("all");
                  setTentFilter("all");
                  setNewOnly(false);
                  setSelectedTags([]);
                  setSelectedEvent(e.target.value);
                }}
              >
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}{ev.start_date ? ` · ${new Date(ev.start_date).getFullYear()}` : ""}
                  </option>
                ))}
              </select>
            )}

            {filtered.length > 0 && (
              <button className="av-export-btn" onClick={handleExportExcel}>
                ↓ Export Excel
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="av-stats-row">
          <div className="av-stat">
            <span className="av-stat-value">{applications.length}</span>
            <span className="av-stat-label">Total</span>
          </div>
          <div className="av-stat av-stat--approved">
            <span className="av-stat-value">{approvedCount}</span>
            <span className="av-stat-label">Approved</span>
          </div>
          <div className="av-stat av-stat--pending">
            <span className="av-stat-value">{pendingCount}</span>
            <span className="av-stat-label">Pending</span>
          </div>
          <div className="av-stat av-stat--denied">
            <span className="av-stat-value">{deniedCount}</span>
            <span className="av-stat-label">Denied</span>
          </div>
          <div className="av-stat av-stat--new">
            <span className="av-stat-value">{newVendorCount}</span>
            <span className="av-stat-label">New Vendors</span>
          </div>
        </div>

        {/* Space Choice Pills Bar */}
        <div className="av-tent-pills-bar">
          <span className="av-pills-label">Space Choice:</span>
          <button
            type="button"
            className={`av-tent-pill ${tentFilter === "all" ? "active" : ""}`}
            onClick={() => setTentFilter("all")}
          >
            All Spaces <span className="av-pill-count">{applications.length}</span>
          </button>
          {Object.entries(TENT_DISPLAY_MAP).map(([key, label]) => {
            const count = tentTypeCounts[key] || 0;
            if (count === 0 && tentFilter !== key) return null;
            return (
              <button
                key={key}
                type="button"
                className={`av-tent-pill ${tentFilter === key ? "active" : ""}`}
                onClick={() => setTentFilter(tentFilter === key ? "all" : key)}
              >
                {label} <span className="av-pill-count">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Filter Toolbar */}
        <div className="av-filters">
          <input
            className="av-search"
            type="search"
            placeholder="Search by brand, name, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <select
            className="av-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="on_hold">On Hold</option>
            <option value="denied">Denied</option>
          </select>

          <div className="av-sort-toggle">
            <button
              type="button"
              className={`av-sort-btn ${sortBy === "alpha" ? "active" : ""}`}
              onClick={() => setSortBy("alpha")}
            >
              A – Z
            </button>
            <button
              type="button"
              className={`av-sort-btn ${sortBy === "chrono" ? "active" : ""}`}
              onClick={() => setSortBy("chrono")}
            >
              Chronological
            </button>
            
          </div>

          <label className="av-checkbox-label">
            <input
              type="checkbox"
              checked={newOnly}
              onChange={(e) => setNewOnly(e.target.checked)}
            />
            New vendors only
          </label>
        </div>

        {/* Category Tag Pills */}
        {allTags.length > 0 && (
          <div className="av-tag-pills">
            <span className="av-tag-label">Categories:</span>
            {displayTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`av-tag-pill ${selectedTags.includes(tag) ? "av-tag-pill--active" : ""}`}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </button>
            ))}

            {allTags.length > 5 && (
              <button
                type="button"
                className="av-tag-pill av-tag-pill--toggle"
                onClick={() => setShowAllTags((prev) => !prev)}
              >
                {showAllTags ? "Show Top 5" : `+${allTags.length - 5} More`}
              </button>
            )}

            {selectedTags.length > 0 && (
              <button
                type="button"
                className="av-tag-pill av-tag-pill--clear"
                onClick={() => setSelectedTags([])}
              >
                Clear tags ×
              </button>
            )}
          </div>
        )}

        {/* Content View */}
        {loading ? (
          <div className="av-loading">
            <div className="av-loading-spinner" />
            <span>Loading applications…</span>
          </div>
        ) : !selectedEvent ? (
          <div className="av-empty">
            <p className="av-empty-title">No events found</p>
            <p className="av-empty-sub">Create an event first to manage vendor applications.</p>
          </div>
        ) : filtered.length > 0 ? (
          sortBy === "alpha" && groupedByLetter ? (
            <div className="av-alpha-grouped-container">
              {Object.keys(groupedByLetter).sort().map((letter) => (
                <section
                  key={letter}
                  className="av-letter-group"
                  ref={(el) => (sectionRefs.current[letter] = el)}
                >
                  <div className="av-letter-header">
                    <h2>{letter}</h2>
                    <span className="av-letter-count">{groupedByLetter[letter].length} vendor(s)</span>
                  </div>
                  <div className="av-grid">
                    {groupedByLetter[letter].map((application) => (
                      <VendorCard
                        key={application.id}
                        application={application}
                        vendor={application.vendor}
                        tentDisplay={TENT_DISPLAY_MAP[application.tent_type] || application.tent_type}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDeleteApplication}
                        isNewVendor={application.is_first_application}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="av-grid">
              {filtered.map((application) => (
                <VendorCard
                  key={application.id}
                  application={application}
                  vendor={application.vendor}
                  tentDisplay={TENT_DISPLAY_MAP[application.tent_type] || application.tent_type}
                  onStatusChange={handleStatusChange}
                  onDelete={handleDeleteApplication}
                  isNewVendor={application.is_first_application}
                />
              ))}
            </div>
          )
        ) : (
          <div className="av-empty">
            <p className="av-empty-title">No applications found</p>
            <p className="av-empty-sub">Try adjusting your search, space pill, or filters.</p>
          </div>
        )}

        {/* Decoupled Portal Alphabet Rail */}
        <AlphabetScrubberRail
          visible={sortBy === "alpha" && filtered.length > 0}
          activeLetter={activeLetter}
          onSelectLetter={handleSelectLetter}
        />

      </div>
    </div>
  );
};

export default AdminVendors;